import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

type EmptyStateVariant = 'noThreads' | 'noResults' | 'error' | 'myThreads' | 'savedThreads';

interface CorkBoardEmptyStateProps {
  variant: EmptyStateVariant;
  onRetry?: () => void;
  onCreatePost?: () => void;
  onBrowseFeed?: () => void;
}

const VARIANT_ICONS = {
  noThreads: RESIDENTS_SVG_COMPONENTS.BOARD,
  noResults: RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS,
  error: RESIDENTS_SVG_COMPONENTS.WARNING,
  myThreads: RESIDENTS_SVG_COMPONENTS.CONVERSATION,
  savedThreads: RESIDENTS_SVG_COMPONENTS.STAR,
} as const;

export const CorkBoardEmptyState: React.FC<CorkBoardEmptyStateProps> = memo(({
  variant,
  onRetry,
  onCreatePost,
  onBrowseFeed,
}) => {
  const { colors } = useThemeColors();

  const VARIANT_CONFIG: Record<EmptyStateVariant, { title: string; subtitle: string; iconColor: string }> = {
    noThreads: {
      title: 'No posts yet',
      subtitle: 'Be the first to share something with your community!',
      iconColor: colors.text.muted,
    },
    noResults: {
      title: 'No results found',
      subtitle: 'Try adjusting your filters or search terms.',
      iconColor: colors.text.muted,
    },
    error: {
      title: 'Something went wrong',
      subtitle: 'We couldn\'t load the feed. Please try again.',
      iconColor: colors.status.inProgressText,
    },
    myThreads: {
      title: 'You haven\'t posted yet',
      subtitle: 'Your community posts will appear here.',
      iconColor: colors.text.muted,
    },
    savedThreads: {
      title: 'No saved posts',
      subtitle: 'Posts you bookmark will appear here.',
      iconColor: colors.text.muted,
    },
  };

  const config = VARIANT_CONFIG[variant];
  const IconComponent = VARIANT_ICONS[variant];

  return (
    <View style={styles.container} accessibilityRole={variant === 'error' ? 'alert' : undefined}>
      <View style={[styles.iconWrapper, { backgroundColor: colors.card.bg }]}>
        <IconComponent width={36} height={36} color={config.iconColor} />
      </View>
      <Text style={[styles.title, { color: colors.text.primary }]}>{config.title}</Text>
      <Text style={[styles.subtitle, { color: colors.text.dim }]}>{config.subtitle}</Text>

      {(variant === 'noThreads' || variant === 'myThreads') && onCreatePost && (
        <TouchableOpacity style={styles.ctaButton} onPress={onCreatePost} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Create a post">
          <Text style={styles.ctaText}>Create a Post</Text>
        </TouchableOpacity>
      )}

      {variant === 'error' && onRetry && (
        <TouchableOpacity style={styles.retryButton} onPress={onRetry} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Try again">
          <Text style={styles.retryText}>Try Again</Text>
        </TouchableOpacity>
      )}

      {variant === 'savedThreads' && onBrowseFeed && (
        <TouchableOpacity style={styles.ctaButton} onPress={onBrowseFeed} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Browse feed">
          <Text style={styles.ctaText}>Browse Feed</Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  iconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  ctaButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  ctaText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  retryButton: {
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlue,
  },
});
